import React from "react";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import {
  Button,
  Box,
  Typography,
  IconButton,
  useMediaQuery
} from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    paddingTop: theme.spacing(2),
    border: "none",
    [theme.breakpoints.down("sm")]: {
      "&:last-child": {
        paddingRight: theme.spacing(0)
      }
    }
  },
  button: {
    fontWeight: "normal",
    minWidth: theme.spacing(4.5),
    padding: theme.spacing(0.5, 1),
    margin: theme.spacing(0, 0.25),
    [theme.breakpoints.down("lg")]: {
      minWidth: theme.spacing(0)
    }
  },
  pageLinkActive: {
    fontWeight: "bold"
  },
  buttonInactive: {
    color: "#949494",
    backgroundColor: theme.palette.secondary[200]
  },
  displayedRows: {
    paddingRight: theme.spacing(2)
  },
  arrowIcon: {
    padding: theme.spacing(1),
    [theme.breakpoints.down("lg")]: {
      padding: theme.spacing(0)
    }
  }
}));

const Pagination = props => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const {
    gotoPage,
    previousPage,
    nextPage,
    pageIndex,
    canNextPage,
    canPreviousPage,
    pageSize,
    setPageSize,
    itemType,
    rowsPerPageOptions,
    totalCount
  } = props;

  const numPages = Math.ceil(totalCount / pageSize);
  const smallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const maxNumPageLinks = smallScreen ? 4 : 10;
  const numPerPage = (pageIndex - 1) * pageSize + 1;
  function getPageLinksList() {
    const handlePageLinkClick = e =>
      gotoPage(parseInt(e.currentTarget.getAttribute("data-index")) - 1);

    let startPage = 0;
    let endPage = 0;

    // Shows the before and after page numbers that are closest to the current page
    if (pageIndex > maxNumPageLinks / 2) {
      endPage = Math.min(numPages, pageIndex + maxNumPageLinks / 2) + 1;
    } else {
      endPage = Math.min(numPages, maxNumPageLinks) + 1;
    }
    startPage = Math.max(1, endPage - maxNumPageLinks);

    let pageLinks = [];
    let index = startPage;
    while (index < endPage) {
      pageLinks.push(
        <Button
          color="primary"
          classes={{
            root: classes.button,
            text: pageIndex === index ? classes.pageLinkActive : ""
          }}
          key={index}
          data-index={index}
          onClick={handlePageLinkClick}
        >
          {index}
        </Button>
      );
      index++;
    }

    return pageLinks;
  }

  const createRowsPerPageOptionsButtons = () => {
    return rowsPerPageOptions.map((option, index) => (
      <Button
        size="small"
        variant="contained"
        color={option === pageSize ? "primary" : "secondary"}
        classes={{
          root: classes.button,
          containedSecondary: classes.buttonInactive
        }}
        key={index}
        data-index={option}
        onClick={e =>
          setPageSize(parseInt(e.currentTarget.getAttribute("data-index")))
        }
      >
        {option}
      </Button>
    ));
  };

  const labelDisplayedRows = ({ from, to, numRows }) =>
    `${from}-${to} ${t("table.of")} ${numRows}`;

  return (
    <Box className={classes.root}>
      <Box p={{ xs: 1, md: 0 }}>
        <IconButton
          color="primary"
          classes={{ root: classes.arrowIcon }}
          onClick={previousPage}
          disabled={!canPreviousPage}
          size="large"
        >
          <ArrowLeftIcon />
        </IconButton>
        {getPageLinksList()}
        <IconButton
          color="primary"
          classes={{ root: classes.arrowIcon }}
          onClick={nextPage}
          disabled={!canNextPage}
          size="large"
        >
          <ArrowRightIcon />
        </IconButton>
      </Box>
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        alignItems="center"
        pt={{ xs: 1, md: 0 }}
      >
        {itemType && (
          <Box mr={{ xs: 0, sm: 3 }} mb={{ xs: 3, sm: 0 }}>
            <Typography
              variant="subtitle2"
              color="textPrimary"
            >{`${pageSize} ${itemType + (pageSize !== 1 ? "s" : "")} ${t(
              "table.numItemsFound"
            )}`}</Typography>
          </Box>
        )}
        <Box display="flex" alignItems="center">
          <Typography classes={{ root: classes.displayedRows }} variant="body2">
            {labelDisplayedRows({
              from: totalCount < numPerPage ? totalCount : numPerPage,
              to: Math.min(pageIndex * pageSize, totalCount),
              numRows: totalCount
            })}
          </Typography>
          {createRowsPerPageOptionsButtons()}
        </Box>
      </Box>
    </Box>
  );
};
export default Pagination;
